import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Calendar,
  Clock,
  MapPin,
  User,
  Search,
  Filter,
  Star,
  Download,
  Share2,
  ChevronRight,
  Sparkles,
  Info,
  Building2,
  Utensils,
  Church,
  Mic,
  Users,
  Compass,
  Award,
  CheckCircle2,
  Printer,
  X,
  ExternalLink
} from 'lucide-react';

// --- DATOS DE LA AGENDA ---
const EVENT_HEADER = {
  title: "VIII ENCUENTRO DE LA RED DE COMUNICACIÓN DE ODUCAL",
  subtitle: "Narrar el bien común: el desafío comunicacional de las universidades católicas",
  host: "Universidad Técnica Particular de Loja (UTPL)",
  location: "Loja, Ecuador",
  dateRange: "16 - 18 de Noviembre, 2026"
};

const CATEGORIES = {
  ALL: { label: "Todas", color: "bg-slate-100 text-slate-700 hover:bg-slate-200" },
  CONFERENCE: { label: "Conferencia", color: "bg-[#004270]/10 text-[#004270] border-[#004270]/30", icon: Mic },
  TALK: { label: "Conversatorio", color: "bg-indigo-50 text-indigo-800 border-indigo-200", icon: Users },
  WORKSHOP: { label: "Taller / Espacio", color: "bg-amber-50 text-amber-800 border-amber-300", icon: Sparkles },
  RELIGIOUS: { label: "Eucaristía", color: "bg-purple-50 text-purple-800 border-purple-200", icon: Church },
  CULTURAL: { label: "Cultural / Social", color: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: Compass },
  BREAK: { label: "Receso / Almuerzo", color: "bg-slate-100 text-slate-700 border-slate-200", icon: Utensils },
  PROTOCOL: { label: "Protocolo", color: "bg-sky-50 text-sky-800 border-sky-200", icon: Award }
};

const DAYS_DATA = [
  {
    id: "day1",
    dateStr: "Lunes, 16 de noviembre de 2026",
    shortDate: "16 NOV",
    dayName: "Lunes",
    events: [
      {
        id: "d1-1",
        time: "09:00",
        endTime: "09:15",
        title: "Registro",
        category: "PROTOCOL",
        location: "Hall Principal - UTPL",
        description: "Acreditación y entrega de material informativo a las delegaciones participantes."
      },
      {
        id: "d1-2",
        time: "09:15",
        endTime: "09:45",
        title: "Acto inaugural",
        category: "PROTOCOL",
        location: "Auditorio Principal UTPL",
        description: "Bienvenida oficial al VIII Encuentro de la Red de Comunicación de ODUCAL."
      },
      {
        id: "d1-3",
        time: "09:45",
        endTime: "10:45",
        title: 'Conferencia magistral inaugural: "Comunicar bien y narrar el bien: \'reparar las redes\' en el tiempo de la Inteligencia Artificial"',
        category: "CONFERENCE",
        location: "Auditorio Principal UTPL",
        speaker: {
          name: "Dr. Óscar Elizalde Prada",
          roles: [
            "Director de la Oficina de Información y Comunicación de los Hermanos de La Salle",
            "Director del CPC del CELAM",
            "Consultor del Dicasterio para la Comunicación del Vaticano",
            "Comisión de comunicación del Sínodo"
          ],
          bio: "Experto internacional en comunicación eclesial e institucional y gestión de información estratégica en organizaciones socio-religiosas."
        },
        highlight: true
      },
      {
        id: "d1-4",
        time: "10:45",
        endTime: "11:15",
        title: "Receso (Coffee break)",
        category: "BREAK",
        location: "Plaza Central UTPL"
      },
      {
        id: "d1-5",
        time: "11:15",
        endTime: "13:00",
        title: "Visita guiada al campus UTPL",
        category: "CULTURAL",
        location: "Campus Central UTPL",
        description: "Recorrido por los principales centros de innovación, laboratorios y espacios académicos."
      },
      {
        id: "d1-6",
        time: "13:00",
        endTime: "14:30",
        title: "Almuerzo",
        category: "BREAK",
        location: "Comedor Universitario"
      },
      {
        id: "d1-7",
        time: "14:30",
        endTime: "17:30",
        title: "Exposición de buenas prácticas",
        category: "WORKSHOP",
        location: "Salones Multimediales",
        description: "Presentación de casos de éxito y proyectos universitarios sobre comunicación del bien común de la red ODUCAL."
      },
      {
        id: "d1-8",
        time: "17:30",
        endTime: "18:00",
        title: "Fin de la jornada",
        category: "PROTOCOL",
        location: "Auditorio Principal"
      },
      {
        id: "d1-9",
        time: "18:00",
        endTime: "20:00",
        title: "Recorrido guiado por el Festival Internacional de Artes Vivas (proyectos UTPL)",
        category: "CULTURAL",
        location: "Centro Histórico de Loja",
        description: "Experiencia cultural y artística en las calles de Loja conociendo intervenciones de proyectos UTPL.",
        highlight: true
      }
    ]
  },
  {
    id: "day2",
    dateStr: "Martes, 17 de noviembre de 2026",
    shortDate: "17 NOV",
    dayName: "Martes",
    events: [
      {
        id: "d2-1",
        time: "08:00",
        endTime: "09:00",
        title: "Eucaristía",
        category: "RELIGIOUS",
        location: "Capilla Universitaria UTPL"
      },
      {
        id: "d2-2",
        time: "09:00",
        endTime: "10:15",
        title: 'Conferencia magistral: "Comunicar desde el espíritu católico: inspiración y carisma al servicio del bien común"',
        category: "CONFERENCE",
        location: "Auditorio Principal UTPL",
        speaker: {
          name: "Dra. Cristina Díaz de la Cruz",
          roles: [
            "Vicerrectora Académica de la Universidad Técnica Particular de Loja"
          ],
          bio: "Destacada académica enfocada en la formación humanista integral y la integración del carisma institucional en la propuesta educativa."
        },
        highlight: true
      },
      {
        id: "d2-3",
        time: "10:15",
        endTime: "10:45",
        title: "Receso",
        category: "BREAK",
        location: "Foyer del Auditorio"
      },
      {
        id: "d2-4",
        time: "10:45",
        endTime: "11:45",
        title: 'Conversatorio: "Alianzas para comunicar la universidad: creatividad, estrategia y reputación"',
        category: "TALK",
        location: "Auditorio Principal UTPL",
        description: "Mesa de diálogo interactiva sobre posicionamiento, reputación e impacto social colaborativo."
      },
      {
        id: "d2-5",
        time: "11:45",
        endTime: "13:00",
        title: "Espacio creativo",
        category: "WORKSHOP",
        location: "Laboratorio de Innovación Social",
        description: "Dinámicas colaborativas e ideación de proyectos transversales para la Red."
      },
      {
        id: "d2-6",
        time: "13:00",
        endTime: "14:30",
        title: "Almuerzo",
        category: "BREAK",
        location: "Comedor Universitario"
      },
      {
        id: "d2-7",
        time: "14:30",
        endTime: "17:30",
        title: "Exposición de buenas prácticas",
        category: "WORKSHOP",
        location: "Salones Multimediales",
        description: "Muestras de estrategias audiovisuales, comunicación digital e impacto social en universidades socias."
      },
      {
        id: "d2-8",
        time: "17:30",
        endTime: "18:00",
        title: "Fin de la jornada",
        category: "PROTOCOL",
        location: "Auditorio Principal"
      },
      {
        id: "d2-9",
        time: "20:00",
        endTime: "22:30",
        title: "Cena oficial del Encuentro",
        category: "CULTURAL",
        location: "Salón de Eventos Institutional",
        description: "Encuentro fraternal y cena oficial de integración para delegaciones e invitados.",
        highlight: true
      }
    ]
  },
  {
    id: "day3",
    dateStr: "Miércoles, 18 de noviembre de 2026",
    shortDate: "18 NOV",
    dayName: "Miércoles",
    events: [
      {
        id: "d3-1",
        time: "08:00",
        endTime: "09:00",
        title: "Eucaristía",
        category: "RELIGIOUS",
        location: "Capilla Universitaria UTPL"
      },
      {
        id: "d3-2",
        time: "09:00",
        endTime: "10:30",
        title: 'Conferencia: "IA, datos y confianza para habilitar la comunicación del bien común en una universidad cada vez más digital"',
        category: "CONFERENCE",
        location: "Auditorio Principal UTPL",
        speaker: {
          name: "Dr. Nelson Piedra Pullaguari",
          roles: [
            "Director General de TI y Transformación Digital de la Universidad Técnica Particular de Loja"
          ],
          bio: "Experto en tecnologías de la información, inteligencia artificial, analítica de datos y gobernanza digital en la educación superior."
        },
        highlight: true
      },
      {
        id: "d3-3",
        time: "10:30",
        endTime: "11:00",
        title: "Receso",
        category: "BREAK",
        location: "Foyer del Auditorio"
      },
      {
        id: "d3-4",
        time: "11:00",
        endTime: "11:20",
        title: 'Microconferencia: "Detrás de cada dato, una persona. IA y marketing humanista"',
        category: "CONFERENCE",
        location: "Auditorio Principal UTPL",
        speaker: {
          name: "Leonardo Sánchez",
          roles: [
            "Gerente de Marketing de la Universidad Técnica Particular de Loja"
          ],
          bio: "Especialista en desarrollo de marca, marketing de propósito y comunicación centrada en las personas."
        }
      },
      {
        id: "d3-5",
        time: "11:20",
        endTime: "12:30",
        title: 'Conversatorio: "El nuevo mandato de la comunicación universitaria: la mirada de la gobernanza sobre propósito, comunicación e impacto"',
        category: "TALK",
        location: "Auditorio Principal UTPL",
        description: "Mesa directiva con líderes universitarios de América Latina y el Caribe."
      },
      {
        id: "d3-6",
        time: "12:30",
        endTime: "13:00",
        title: "Acto de clausura del encuentro",
        category: "PROTOCOL",
        location: "Auditorio Principal UTPL",
        description: "Conclusiones del Encuentro, lectura de compromisos ODUCAL y cierre."
      },
      {
        id: "d3-7",
        time: "13:00",
        endTime: "14:30",
        title: "Almuerzo",
        category: "BREAK",
        location: "Comedor Universitario"
      },
      {
        id: "d3-8",
        time: "Tarde",
        endTime: "",
        title: "Actividad complementaria opcional: Visita guiada a Loja",
        category: "CULTURAL",
        location: "Puntos emblemáticos de Loja",
        description: "Recorrido turístico por la ciudad cultural de Loja.",
        highlight: true
      }
    ]
  }
];

export default function App() {
  const [activeTab, setActiveTab] = useState("day1");
  const [savedEvents, setSavedEvents] = useState([]);
  const [activeSpeakerModal, setActiveSpeakerModal] = useState(null);
  const [toastMessage, setToastMessage] = useState(null);

  const triggerToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const toggleSaveEvent = (eventId) => {
    setSavedEvents((prev) => {
      const exists = prev.includes(eventId);
      if (exists) {
        triggerToast("Evento removido de tus guardados");
        return prev.filter((id) => id !== eventId);
      } else {
        triggerToast("¡Evento guardado con éxito!");
        return [...prev, eventId];
      }
    });
  };

  const currentDay = DAYS_DATA.find((d) => d.id === activeTab) || DAYS_DATA[0];

  const downloadICS = (event) => {
    const dayMap = { day1: '16', day2: '17', day3: '18' };
    const dateNum = dayMap[activeTab] || '16';
    const cleanTime = event.time.includes(':') ? event.time.replace(':', '') : '0900';

    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//ODUCAL UTPL//Agenda VIII Encuentro//ES
BEGIN:VEVENT
SUMMARY:${event.title}
DESCRIPTION:${event.description || 'VIII ENCUENTRO DE LA RED DE COMUNICACIÓN DE ODUCAL'}
LOCATION:${event.location || 'UTPL, Loja, Ecuador'}
DTSTART:202611${dateNum}T${cleanTime}00Z
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.setAttribute('download', `ODUCAL-${event.title.slice(0, 15)}.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    triggerToast("Archivo .ics generado para tu calendario");
  };

  return (
    <div className="min-h-screen bg-white text-slate-800 font-sans pb-20 selection:bg-[#febe10] selection:text-[#004270]">
      {/* Toast Notification */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-6 right-6 z-50 bg-[#004270] text-white px-5 py-3 rounded-xl shadow-2xl font-medium text-sm flex items-center space-x-3 border-l-4 border-[#febe10]"
          >
            <CheckCircle2 className="w-5 h-5 text-[#febe10]" />
            <span>{toastMessage}</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* TABS SUPERIORES POR DÍA (STICKY) */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md shadow-sm border-b border-slate-200 print:hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-8">
          <div className="flex space-x-2 sm:space-x-4 overflow-x-auto py-3 scrollbar-none justify-start sm:justify-center">
            {DAYS_DATA.map((day) => {
              const isActive = activeTab === day.id;
              return (
                <button
                  key={day.id}
                  onClick={() => setActiveTab(day.id)}
                  className={`relative flex items-center gap-3 px-5 py-2.5 rounded-xl text-left transition-all ${
                    isActive
                      ? 'text-[#004270] font-bold'
                      : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                  }`}
                >
                  <div
                    className={`text-center px-2.5 py-1 rounded-md text-xs font-black transition-colors ${
                      isActive ? 'bg-[#febe10] text-[#004270]' : 'bg-slate-100 text-slate-600'
                    }`}
                  >
                    {day.shortDate}
                  </div>
                  <div>
                    <div className="text-sm font-semibold">{day.dayName}</div>
                  </div>

                  {isActive && (
                    <motion.div
                      layoutId="activeTabUnderline"
                      className="absolute bottom-0 left-0 right-0 h-1 bg-[#004270] rounded-t-full"
                      transition={{ type: "spring", stiffness: 500, damping: 35 }}
                    />
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* CONTENIDO PRINCIPAL - CRONOGRAMA VERTICAL */}
      <main className="max-w-4xl mx-auto px-4 sm:px-8 pt-8">
        {/* Título del Día Activo */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-200 mb-8">
          <h2 className="text-xl sm:text-2xl font-black text-[#004270] flex items-center gap-2">
            <Calendar className="w-6 h-6 text-[#febe10]" />
            {currentDay.dateStr}
          </h2>
          <span className="text-xs font-semibold text-[#004270] bg-[#004270]/5 px-3 py-1 rounded-full border border-[#004270]/10">
            {currentDay.events.length} actividades
          </span>
        </div>

        {/* LISTADO VERTICAL (TIMELINE) */}
        <div className="relative pl-4 sm:pl-8 border-l-2 border-[#004270]/20 space-y-6 sm:space-y-8">
          {currentDay.events.map((event, idx) => {
            const categoryInfo = CATEGORIES[event.category] || CATEGORIES.PROTOCOL;
            const IconComponent = categoryInfo.icon || Clock;
            const isSaved = savedEvents.includes(event.id);

            return (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, delay: idx * 0.04 }}
                className="relative group"
              >
                {/* Nodo conector en el Timeline */}
                <div
                  className={`absolute -left-[25px] sm:-left-[41px] top-5 w-4 h-4 sm:w-5 sm:h-5 rounded-full border-4 border-white transition-transform group-hover:scale-125 ${
                    event.highlight ? 'bg-[#febe10] shadow-md ring-2 ring-[#febe10]/40' : 'bg-[#004270]'
                  }`}
                />

                {/* Tarjeta del evento */}
                <div
                  className={`rounded-2xl p-5 sm:p-6 transition-all border ${
                    event.highlight
                      ? 'bg-gradient-to-r from-amber-50/60 to-white border-[#febe10] shadow-md'
                      : 'bg-white border-slate-200 hover:border-[#004270]/40 hover:shadow-md'
                  }`}
                >
                  {/* Fila superior: Hora, Etiqueta y Acciones */}
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                    <div className="flex items-center gap-2">
                      <span className="inline-flex items-center gap-1.5 font-mono text-xs font-black px-2.5 py-1 rounded-md bg-[#004270] text-white">
                        <Clock className="w-3 h-3 text-[#febe10]" />
                        {event.time} {event.endTime ? `- ${event.endTime}` : ''}
                      </span>

                      <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-0.5 rounded-md border ${categoryInfo.color}`}>
                        <IconComponent className="w-3 h-3" />
                        {categoryInfo.label}
                      </span>
                    </div>

               
                  </div>

                  {/* Título de la actividad */}
                  <h3 className="text-base sm:text-lg font-bold text-[#004270] mb-2 leading-snug">
                    {event.title}
                  </h3>

                  {/* Descripción */}
                  {event.description && (
                    <p className="text-xs sm:text-sm text-slate-600 mb-3 leading-relaxed">
                      {event.description}
                    </p>
                  )}

                  {/* Tarjeta interactiva del ponente */}
                  {event.speaker && (
                    <div
                      onClick={() => setActiveSpeakerModal(event.speaker)}
                      className="mt-3 p-3 rounded-xl bg-slate-50 border border-slate-200 hover:border-[#004270]/40 hover:bg-slate-100/70 cursor-pointer transition-all flex items-center justify-between group/sp"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-[#004270] text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-sm border border-[#febe10]">
                          <User className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="text-xs font-bold text-[#004270] group-hover/sp:underline">
                            {event.speaker.name}
                          </div>
                          <div className="text-[11px] text-slate-500 line-clamp-1">
                            {event.speaker.roles[0]}
                          </div>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-slate-400 group-hover/sp:translate-x-1 transition-transform" />
                    </div>
                  )}

                  {/* Ubicación */}
                  {event.location && (
                    <div className="mt-3 pt-2 border-t border-slate-100 flex items-center gap-1.5 text-xs text-slate-500 font-medium">
                      <MapPin className="w-3.5 h-3.5 text-[#004270]" />
                      <span>{event.location}</span>
                    </div>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>
      </main>

      {/* MODAL DETALLES DEL PONENTE */}
      <AnimatePresence>
        {activeSpeakerModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm print:hidden">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl max-w-lg w-full p-6 sm:p-8 relative shadow-2xl border border-slate-200 overflow-hidden"
            >
              <button
                onClick={() => setActiveSpeakerModal(null)}
                className="absolute top-4 right-4 p-2 rounded-full bg-slate-100 text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-all"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex items-center gap-4 mb-6">
                <div className="w-14 h-14 rounded-2xl bg-[#004270] text-white flex items-center justify-center font-bold text-2xl shadow-md border-2 border-[#febe10]">
                  <User className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-[#004270]">
                    {activeSpeakerModal.name}
                  </h3>
                  <p className="text-xs text-[#febe10] font-bold uppercase tracking-wider bg-[#004270] px-2 py-0.5 rounded inline-block mt-1">
                    Ponente Invitado
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                    Cargos e Instituciones
                  </h4>
                  <ul className="space-y-1.5">
                    {activeSpeakerModal.roles.map((role, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-xs text-slate-700 font-medium">
                        <span className="w-1.5 h-1.5 rounded-full bg-[#febe10] mt-1.5 shrink-0" />
                        <span>{role}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {activeSpeakerModal.bio && (
                  <div className="pt-3 border-t border-slate-100">
                    <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">
                      Reseña
                    </h4>
                    <p className="text-xs text-slate-600 leading-relaxed">
                      {activeSpeakerModal.bio}
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 pt-4 border-t border-slate-100 flex justify-end">
                <button
                  onClick={() => setActiveSpeakerModal(null)}
                  className="px-5 py-2 rounded-xl bg-[#004270] hover:bg-[#004270]/90 text-white font-bold text-xs transition-all shadow-sm"
                >
                  Cerrar
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}